import React, { useState } from "react";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/react-hooks";
import Button from "../../components/form/Button";
import { padLeft } from "../../helpers/functions";

const EXPORT_REPAIR_REPORT = gql`
  query EXPORT_REPAIR_REPORT($where: line_item_bool_exp!) {
    lineItem(where: $where) {
      id
      headline
      price
      articleNumber
      costEstimate {
        taxRate
        currency
        approvedAt
        createdAt
        repair {
          id
          repairNumber
          returnDate
          sentTo
          status
          createdAt
        }
      }
    }
  }
`;

const regex = /^(.*?),?\s*(?:[|]?\s*SN[:\s]*)([A-Za-z0-9\-\s]+)/;

function escapeCSVValue(value) {
  const normalizedValue = value == null ? "" : String(value);
  return `"${normalizedValue.replace(/"/g, '""')}"`;
}

function convertToCSV(data) {
  if (!data.length) {
    return "";
  }

  const headers = Object.keys(data[0]);

  return [
    headers.join(","),
    ...data.map((row) => headers.map((header) => escapeCSVValue(row[header])).join(",")),
  ].join("\n");
}

function formatDateGerman(dateString) {
  if (!dateString) {
    return "N/A";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return new Intl.DateTimeFormat("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", "");
}

function formatDateOnlyGerman(dateString) {
  if (!dateString) {
    return "N/A";
  }

  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return new Intl.DateTimeFormat("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function downloadCSV(filename, csvContent) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function toStartOfDayISOString(dateString) {
  return new Date(`${dateString}T00:00:00`).toISOString();
}

function toExclusiveEndOfDayISOString(dateString) {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}

function buildWhere(startDate, endDate) {
  const createdAt: {
    _gte?: string;
    _lt?: string;
  } = {};

  if (startDate) {
    createdAt._gte = toStartOfDayISOString(startDate);
  }

  if (endDate) {
    createdAt._lt = toExclusiveEndOfDayISOString(endDate);
  }

  const repairFilter = {
    deleted: { _neq: true },
    ...(Object.keys(createdAt).length ? { createdAt } : {}),
  };

  return {
    mandatory: { _eq: false },
    headline: { _ilike: "%SN%" },
    costEstimate: {
      repair: repairFilter,
    },
  };
}

function formatSelectedRange(startDate, endDate) {
  if (!startDate && !endDate) {
    return "Zeitraum: Alles bisher";
  }

  const formattedStart = startDate ? formatDateOnlyGerman(startDate) : "Von Anfang an";
  const formattedEnd = endDate ? formatDateOnlyGerman(endDate) : "Bis jetzt";

  return `Zeitraum: ${formattedStart} bis ${formattedEnd}`;
}

export default function AdminRepairReportExport() {
  const [message, setMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [runExport, { loading }] = useLazyQuery(EXPORT_REPAIR_REPORT, {
    fetchPolicy: "network-only",
    onCompleted(data) {
      const formattedData = data.lineItem
        .map((item) => {
          const match = item.headline.match(regex);
          if (!match) {
            return null;
          }

          const repair = item.costEstimate.repair;
          const year = new Date(repair.createdAt).getFullYear() - 2000;

          return {
            Eingabe: item.headline,
            Produkt: match[1].trim(),
            Seriennummer: match[2].trim(),
            Preis: `${(item.price / 100).toFixed(2)} ${item.costEstimate.currency}`,
            Steuersatz: `${item.costEstimate.taxRate}%`,
            Artikelnummer: item.articleNumber,
            Reparaturnummer: `R${year}-${padLeft(repair.repairNumber, 4)}`,
            "Erstellt am": formatDateGerman(repair.createdAt),
            "Angebot erstellt am": formatDateGerman(item.costEstimate.createdAt),
            "Genehmigt am": formatDateGerman(item.costEstimate.approvedAt),
            Reparaturort: repair.sentTo || "N/A",
            "Zurückgesendet am": formatDateGerman(repair.returnDate),
            Status: repair.status,
          };
        })
        .filter(Boolean);

      const csv = convertToCSV(formattedData);
      if (!csv) {
        setMessage("Es wurden keine exportierbaren Datensätze gefunden.");
        return;
      }

      const dateSuffix = new Date().toISOString().slice(0, 10);
      downloadCSV(`repair-user-report-${dateSuffix}.csv`, csv);
      setMessage(`${formattedData.length} Datensätze exportiert.`);
    },
    onError(error) {
      setMessage(`Export fehlgeschlagen: ${error.message}`);
    },
  });
  const hasInvalidRange = Boolean(startDate && endDate && startDate > endDate);

  return (
    <div className="mb-6 overflow-hidden bg-white rounded shadow-md">
      <div className="px-6 py-4">
        <div className="mb-2 text-xl font-bold">Reparaturreport</div>
        <p className="mb-4 text-gray-700">
          Exportiert Handstück-Datensätze inklusive Genehmigungsdatum und Reparaturort als CSV.
        </p>
        <div className="flex flex-wrap -mx-2">
          <div className="w-full px-2 md:w-auto">
            <label htmlFor="repair-report-start-date" className="block my-2 font-semibold">
              Von
            </label>
            <input
              id="repair-report-start-date"
              className="w-full max-w-xs px-2 py-3 bg-gray-100 border border-gray-400"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div className="w-full px-2 md:w-auto">
            <label htmlFor="repair-report-end-date" className="block my-2 font-semibold">
              Bis
            </label>
            <input
              id="repair-report-end-date"
              className="w-full max-w-xs px-2 py-3 bg-gray-100 border border-gray-400"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>
        <p className="mt-3 text-gray-700">{formatSelectedRange(startDate, endDate)}</p>
        {hasInvalidRange && <p className="mt-2 text-red-400">Das Enddatum darf nicht vor dem Startdatum liegen.</p>}
        <Button
          className="mt-4"
          disabled={loading || hasInvalidRange}
          onClick={() => {
            setMessage("");
            runExport({
              variables: {
                where: buildWhere(startDate, endDate),
              },
            });
          }}
        >
          {loading ? "Export wird erstellt..." : "Reparaturreport herunterladen"}
        </Button>
        {message && <p className="mt-3 text-gray-700">{message}</p>}
      </div>
    </div>
  );
}
